import { SortingState } from "@tanstack/react-table";
import { If } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useDebouncedValue, useSearchParameter } from "hooks";
import { Order, useGetOrders } from "modules/orders";
import { OrderModal, OrdersTable } from "modules/orders/components";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination, SearchInput } from "ui";

import styles from "./CompanyTabShared.module.scss";

interface CompanyOrdersTabProps {
  companyId: string;
}

const CompanyOrdersTab = ({ companyId }: CompanyOrdersTabProps) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue<string>(searchValue, 500);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [openedOrderId, setOpenedOrderId] = useSearchParameter("orderId");

  const requestParams = useMemo(
    () => ({
      page,
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch,
      orderBy: sorting[0]?.id ?? null,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "ASC",
      deliveryCompanyId: companyId
    }),
    [page, debouncedSearch, sorting, companyId]
  );

  const { orders, meta, isLoading, refetch } = useGetOrders(
    requestParams as unknown as Parameters<typeof useGetOrders>[0]
  );

  const totalItems = meta?.itemCount ?? 0;

  return (
    <div className={styles.tab}>
      <div className={styles.tab__toolbar}>
        <div className={styles.tab__toolbarLeft}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t("Search...")}
          />
        </div>
      </div>

      <OrdersTable
        ordersList={orders ?? []}
        isLoading={isLoading}
        sorting={sorting}
        setSorting={setSorting}
        refetch={refetch}
        onAssignDriver={(order: Order) => setOpenedOrderId(order.id)}
      />

      <Pagination
        currentPage={page}
        onChange={setPage}
        perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
        totalItems={totalItems}
      />

      <If condition={!!openedOrderId}>
        <OrderModal
          orderId={String(openedOrderId)}
          isOpen={Boolean(openedOrderId)}
          onClose={() => setOpenedOrderId(null)}
          refetchOrders={refetch}
        />
      </If>
    </div>
  );
};

export default CompanyOrdersTab;
